import { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Chip from '@mui/material/Chip';
import { useAuth0 } from '@auth0/auth0-react';
import { NavLink } from 'react-router-dom';
import { useMe } from '../hooks/useMe';
import { useI18n } from '../i18n';
import LanguageToggle from './LanguageToggle';
import UsersConsole from './UsersConsole';
import ProductsConsole from './ProductsConsole';

const TABS = [
  { to: '/dashboard', key: 'nav.dashboard' },
  { to: '/accounts', key: 'nav.accounts' },
  { to: '/calendar', key: 'nav.calendar' },
  // Cotizaciones sólo aparece para quien puede verlas.
  { to: '/quotes', key: 'nav.quotes', permission: 'read_quotes' },
];

// Igual que ModuleTabs en mpindustries-web: pista blanca, borde fino y la
// pestaña activa entintada en vez de subrayada.
const tabSx = ({ isActive }) => ({
  fontSize: 13.5,
  fontWeight: isActive ? 500 : 450,
  color: isActive ? '#FF6666' : '#847782',
  backgroundColor: isActive ? '#FFF0F0' : 'transparent',
  border: `1px solid ${isActive ? '#E4E0E2' : 'transparent'}`,
  borderRadius: '4px',
  padding: '6px 14px',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
});

export default function Layout({ children }) {
  const { logout, user } = useAuth0();
  const { me } = useMe();
  const { t } = useI18n();
  const [anchor, setAnchor] = useState(null);
  const [consoleOpen, setConsoleOpen] = useState(null);   // 'users' | 'products'

  const canManage = Boolean(me?.permissions?.includes('manage_users'));
  const initials = (me?.display_name || user?.name || user?.email || '?')
    .split(/[\s@._-]/).filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join('');

  const openConsole = (which) => { setAnchor(null); setConsoleOpen(which); };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static">
        <Toolbar sx={{ gap: 2, minHeight: 54 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 16, whiteSpace: 'nowrap' }}>
            {t('layout.brandPrefix')}{' '}
            <Box component="span" sx={{ color: '#FF6666' }}>{t('layout.brandName')}</Box>
          </Typography>

          <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
            {TABS.filter((tab) => !tab.permission
              || me?.permissions?.includes(tab.permission)).map((tab) => (
              <NavLink key={tab.to} to={tab.to} style={tabSx}>{t(tab.key)}</NavLink>
            ))}
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Cambio de idioma siempre visible: dos códigos, sin menú extra. */}
          <LanguageToggle />

          <IconButton
            onClick={(e) => setAnchor(e.currentTarget)}
            size="small"
            aria-label={t('layout.accountMenu')}
          >
            <Avatar sx={{
              width: 30, height: 30, bgcolor: canManage ? '#531253' : '#847782',
              fontSize: 12, fontWeight: 600,
            }}>{initials}</Avatar>
          </IconButton>

          <Menu
            anchorEl={anchor}
            open={Boolean(anchor)}
            onClose={() => setAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { minWidth: 262, border: '1px solid #E4E0E2' } } }}
          >
            <Box sx={{ px: 2, py: 1.2 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                {me?.display_name || user?.name}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#847782', mb: 0.8 }}>
                {me?.email || user?.email}
              </Typography>
              <Chip
                size="small"
                label={me?.app_role ? t(`roles.${me.app_role}`) : t('roles.none')}
                sx={{
                  height: 19, fontSize: 10.5, fontWeight: 600, borderRadius: '3px',
                  backgroundColor: canManage ? '#F3E9F3' : '#EDE6E3',
                  color: canManage ? '#531253' : '#6B5F68',
                }}
              />
            </Box>

            <Divider />

            {canManage && (
              <MenuItem onClick={() => openConsole('users')} sx={{ fontSize: 13.5, py: 1.1 }}>
                {t('layout.usersAndPermissions')}
              </MenuItem>
            )}
            {canManage && (
              <MenuItem onClick={() => openConsole('products')} sx={{ fontSize: 13.5, py: 1.1 }}>
                {t('products.title')}
              </MenuItem>
            )}
            {canManage && <Divider />}

            <MenuItem
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              sx={{ fontSize: 13.5, py: 1.1, color: '#AE5B61' }}
            >
              {t('layout.logout')}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="main" sx={{ flex: 1, p: 3, backgroundColor: '#F5F5F5' }}>
        {children}
      </Box>

      <Dialog
        open={Boolean(consoleOpen)}
        onClose={() => setConsoleOpen(null)}
        maxWidth="md"
        fullWidth
        slotProps={{ paper: { sx: { backgroundColor: '#F5F5F5', border: '1px solid #E4E0E2' } } }}
      >
        <DialogTitle sx={{
          fontSize: 18, fontWeight: 600, borderBottom: '1px solid #E4E0E2',
          backgroundColor: '#fff', display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          {consoleOpen === 'products' ? t('products.title') : t('usersConsole.title')}
          <Box sx={{ flexGrow: 1 }} />
          <Box
            component="button"
            onClick={() => setConsoleOpen(null)}
            sx={{
              border: 'none', background: 'none', cursor: 'pointer',
              fontSize: 20, color: '#847782', lineHeight: 1, fontFamily: 'inherit',
            }}
            aria-label={t('common.close')}
          >×</Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          {consoleOpen === 'users' && <UsersConsole />}
          {consoleOpen === 'products' && <ProductsConsole />}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
